import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { Button, Text } from "@pentabd/ui";

import { yupResolver } from "@hookform/resolvers/yup";

import {
  SOFTWARE_INVENTORY_VALIDATION,
  SoftwareInventoryValidationSchemaType,
  softwareInventoryValidationSchema,
} from "@validations/Inventory/softwareInventory/softwareInventory";

import {
  SelectEmployee,
  SuccessModal,
  ErrorModal,
  InputTextRow,
  SelectControllerRow,
  InputDateControllerRow,
  InputTextAreaRow,
  FileRow,
} from "@components";

import { SoftwareInventoryType } from "@type/inventory/software-inventory";

import { ROUTES } from "@constants/routes";
import {
  convertMultiDocumentSingleFileData,
  revertMultiDocumentSingleFileData,
} from "@utils/documentDataConvert";
import { GENERAL_INFO } from "@validations/HRM/employee/generalInfoValidation";
import SoftwareHistory from "../SoftwareHistory";
import { useSupplier } from "@hooks/inventory/softwareInventory/useSupplier";
import { convertVendorsToSelect } from "@helpers/procurement/convertVendorsToSelect";

const { INVENTORY } = ROUTES;
const { SOFTWARE_INVENTORY } = INVENTORY;

interface SoftwareInventoryCreateUpdateFormPropTypes {
  softwareId: string | undefined;
  SoftwareData: SoftwareInventoryType | null;
  updateSuccess: boolean;
  updateError: any;
  createSoftwareInventory: (
    id: string | number,
    data: SoftwareInventoryType,
  ) => void;
}

export default function SoftwareInventoryCreateUpdateForm({
  softwareId,
  SoftwareData,
  updateError,
  updateSuccess,
  createSoftwareInventory,
}: SoftwareInventoryCreateUpdateFormPropTypes) {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [createErrorMessage, setCreateErrorMessage] = useState("");

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { resData } = useSupplier();
  const { data: suppliers } = resData;

  const registeredName = SOFTWARE_INVENTORY_VALIDATION.OWNER_FROM_ACC;

  const method = useForm<SoftwareInventoryValidationSchemaType>({
    resolver: yupResolver(softwareInventoryValidationSchema),
    values: softwareId
      ? revertMultiDocumentSingleFileData(SoftwareData, [
          SOFTWARE_INVENTORY_VALIDATION.SLA,
          SOFTWARE_INVENTORY_VALIDATION.SUPPORTING_DOCUMENTS,
        ])
      : {},
  });
  const {
    control,
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = method;

  useEffect(() => {
    setValue(
      `${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`,
      SoftwareData?.employee?.unitId as any,
    );
    setValue(
      `${GENERAL_INFO.DESIGNATION}-${registeredName}`,
      SoftwareData?.employee?.positionId as any,
    );
  }, [SoftwareData, setValue, registeredName]);

  const watchMaintenanceParty =
    watch(SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_PARTY) || "";

  const onSubmit: SubmitHandler<SoftwareInventoryValidationSchemaType> = (
    data: SoftwareInventoryValidationSchemaType,
  ) => {
    createSoftwareInventory(
      softwareId ? softwareId : "",
      convertMultiDocumentSingleFileData(data, [
        SOFTWARE_INVENTORY_VALIDATION.SLA,
        SOFTWARE_INVENTORY_VALIDATION.SUPPORTING_DOCUMENTS,
      ]),
    );
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () => {
    navigate(SOFTWARE_INVENTORY);
  };
  const cancelAction = () => {
    navigate(SOFTWARE_INVENTORY);
  };

  useEffect(() => {
    if (updateSuccess) {
      setIsSuccessModalOpen(true);
    }
    if (updateError) {
      setIsErrorModalOpen(true);
      if (updateError.response && updateError.response.data) {
        setCreateErrorMessage(
          updateError.response.data.message || "something was wrong",
        );
      }
    }
  }, [updateSuccess, updateError]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <div className="container p-9 g-10">
      <>
        <SuccessModal isModalOpen={isSuccessModalOpen} action={successAction} />
        <ErrorModal
          subtitle={createErrorMessage}
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>

      <FormProvider {...method}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-column gap-6">
            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.SOFTWARE_NAME"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.SOFTWARE_NAME),
              }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.PURPOSE"
              register={{ ...register(SOFTWARE_INVENTORY_VALIDATION.PURPOSE) }}
            />

            <SelectControllerRow
              label="SOFTWARE_INVENTORY.FORM.SUPPLIER_NAME"
              name={SOFTWARE_INVENTORY_VALIDATION.SUPPLIER_NAME}
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.SUPPLIER_NAME),
              }}
              options={convertVendorsToSelect(suppliers)}
              setValue={setValue}
              control={control}
              error={errors}
              required
            />

            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.SUPPLIER_CONTACT"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.SUPPLIER_CONTACT),
              }}
              error={errors as any}
              required
            />

            <InputDateControllerRow
              control={control}
              label="SOFTWARE_INVENTORY.FORM.PURCHASE_DATE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.PURCHASE_DATE),
              }}
              name={SOFTWARE_INVENTORY_VALIDATION.PURCHASE_DATE}
              errors={errors as any}
              required
            />

            <InputDateControllerRow
              control={control}
              label="SOFTWARE_INVENTORY.FORM.GO_LIVE_DATE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.GO_LIVE_DATE),
              }}
              name={SOFTWARE_INVENTORY_VALIDATION.GO_LIVE_DATE}
              errors={errors as any}
              required
            />

            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.LICENSE_TYPE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.LICENSE_TYPE),
              }}
              error={errors as any}
              required
            />

            <InputDateControllerRow
              control={control}
              label="SOFTWARE_INVENTORY.FORM.LICENSE_RENEWAL_DATE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.LICENSE_RENEWAL_DATE),
              }}
              name={SOFTWARE_INVENTORY_VALIDATION.LICENSE_RENEWAL_DATE}
              errors={errors as any}
              required
            />

            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.PRICE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.PRICE),
              }}
              error={errors as any}
              required
            />

            <InputTextAreaRow
              label="SOFTWARE_INVENTORY.FORM.TENDER_INFO"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.TENDER_INFO),
              }}
            />

            <InputDateControllerRow
              control={control}
              label="SOFTWARE_INVENTORY.FORM.WARRANTY_EXPIRE_DATE"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.WARRANTY_EXPIRE_DATE),
              }}
              name={SOFTWARE_INVENTORY_VALIDATION.WARRANTY_EXPIRE_DATE}
              errors={errors as any}
              required
            />

            <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.WARRANTY_PERIOD"
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.WARRANTY_PERIOD),
              }}
              error={errors as any}
              required
            />

            {/* maintenance party        */}

            <SelectControllerRow
              label="SOFTWARE_INVENTORY.FORM.MAINTENANCE_PARTY"
              name={SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_PARTY}
              register={{
                ...register(SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_PARTY),
              }}
              options={[
                {
                  label: "Third Party",
                  value: "Third Party",
                },
                {
                  label: "ACC",
                  value: "ACC",
                },
              ]}
              setValue={setValue}
              control={control}
              error={errors}
              required
            />

            {watchMaintenanceParty.toString().toLowerCase() ===
            "third party" ? (
              <>
                {" "}
                <SelectControllerRow
                  label="SOFTWARE_INVENTORY.FORM.SUPPLIER_NAME"
                  name={SOFTWARE_INVENTORY_VALIDATION.PARTY_NAME}
                  register={{
                    ...register(SOFTWARE_INVENTORY_VALIDATION.PARTY_NAME),
                  }}
                  options={convertVendorsToSelect(suppliers)}
                  setValue={setValue}
                  control={control}
                  error={errors}
                  required
                />
                <InputDateControllerRow
                  control={control}
                  label="SOFTWARE_INVENTORY.FORM.MAINTENANCE_CONTACT_SIGN_OF_DATE"
                  register={{
                    ...register(
                      SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_CONTACT_SIGN_OF_DATE,
                    ),
                  }}
                  name={
                    SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_CONTACT_SIGN_OF_DATE
                  }
                  errors={errors as any}
                  required
                />
                <InputDateControllerRow
                  control={control}
                  label="SOFTWARE_INVENTORY.FORM.MAINTENANCE_CONTACT_EXPIRY_DATE"
                  register={{
                    ...register(
                      SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_CONTACT_EXPIRE_DATE,
                    ),
                  }}
                  name={
                    SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_CONTACT_EXPIRE_DATE
                  }
                  errors={errors as any}
                  required
                />
                <InputTextRow
                  label="SOFTWARE_INVENTORY.FORM.MAINTENANCE_CONTACT"
                  register={{
                    ...register(
                      SOFTWARE_INVENTORY_VALIDATION.MAINTENANCE_CONTACT,
                    ),
                  }}
                  error={errors as any}
                  required
                />
              </>
            ) : (
              <></>
            )}

            {/* sla file  */}

            <FileRow
              label="SOFTWARE_INVENTORY.FORM.SLA"
              name={SOFTWARE_INVENTORY_VALIDATION.SLA}
              control={control}
              setValue={setValue}
              register={register}
              error={errors}
              isSingle={false}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            <FileRow
              label="SOFTWARE_INVENTORY.FORM.SUPPORTING_DOCUMENTS"
              name={SOFTWARE_INVENTORY_VALIDATION.SUPPORTING_DOCUMENTS}
              control={control}
              setValue={setValue}
              register={register}
              error={errors}
              isSingle={false}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            {/* owner from acc  */}

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("SOFTWARE_INVENTORY.FORM.OWNER_FROM_ACC")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <SelectEmployee
                  selectSingleEmployee
                  registeredName={SOFTWARE_INVENTORY_VALIDATION.OWNER_FROM_ACC}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title"></Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <div className="d-flex justify-content-end gap-8">
                  <Button
                    fill="outline"
                    type="light"
                    size="sm"
                    htmlType="button"
                    onClick={cancelAction}
                  >
                    {t("SOFTWARE_INVENTORY.FORM.CANCEL_BUTTON_TEXT")}
                  </Button>
                  <Button
                    fill="fill"
                    type="info"
                    size="sm"
                    htmlType="submit"
                    // loading={isCreateLoading}
                  >
                    {t("SOFTWARE_INVENTORY.FORM.SAVE_BUTTON_TEXT")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      {SoftwareData && SoftwareData?.softwareHistory && (
        <SoftwareHistory id={softwareId} data={SoftwareData} />
      )}
    </div>
  );
}
